import React from 'react';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useMetadata } from '../../context/MetadataContext';
import { useMessages } from '../../hooks/useMessages';
import styles from './MessagesPanel.module.css';

const MessagesPanel = ({ isOpen, onClose }) => {
  const {
    messages,
    selectedMessage,
    loading,
    error,
    hasMore,
    loadMore,
    openMessage,
    removeMessage,
    clearSelectedMessage,
  } = useMessages(isOpen);
  const { getText } = useMetadata();

  const handleLoadMore = loadMore;

  const handleMessageClick = openMessage;

  const handleDeleteMessage = async (messageKey) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    await removeMessage(messageKey);
  };

  const handleBack = clearSelectedMessage;

  const formatTimestamp = (timestamp) => {
    const now = Date.now() / 1000;
    const diff = now - timestamp;

    if (diff < 60) {
      return 'Just now';
    } else if (diff < 3600) {
      const minutes = Math.floor(diff / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diff < 86400) {
      const hours = Math.floor(diff / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diff < 604800) {
      const days = Math.floor(diff / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return new Date(timestamp * 1000).toLocaleDateString();
    }
  };

  return (
    <div className={`${styles.messagesContainer} ${isOpen ? styles.open : ''}`}>
      <div className={styles.messagesHeader}>
        {selectedMessage ? (
          <>
            <button className={styles.backButton} onClick={handleBack} aria-label="Back to messages">
              <ArrowLeft size={20} />
            </button>
            <h2 className={styles.messagesTitle}>Message</h2>
          </>
        ) : (
          <h2 className={styles.messagesTitle}>
            {getText('common_text.messages')}
          </h2>
        )}
        <button className={styles.closeButton} onClick={onClose} aria-label="Close messages">
          ×
        </button>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}
        </div>
      )}

      {selectedMessage ? (
        // Message Detail View
        <div className={styles.messageDetail}>
          <div className={styles.messageDetailHeader}>
            <h3 className={styles.messageDetailTitle}>{selectedMessage.title}</h3>
            <button
              className={styles.deleteButton}
              onClick={() => handleDeleteMessage(selectedMessage.message_key)}
              aria-label="Delete message"
            >
              <Trash2 size={18} />
            </button>
          </div>
          <div className={styles.messageDetailTimestamp}>
            {formatTimestamp(selectedMessage.created_ts)}
          </div>
          <div className={styles.messageDetailBody}>
            {selectedMessage.body}
          </div>
        </div>
      ) : (
        // Message List View
        <div className={styles.messagesContent}>
          {loading && messages.length === 0 ? (
            <div className={styles.loadingState}>
              {getText('common_text.loading')}...
            </div>
          ) : messages.length === 0 ? (
            <div className={styles.emptyState}>
              {getText('common_text.no_messages')}
            </div>
          ) : (
            <>
              <div className={styles.messageList}>
                {messages.map((message) => (
                  <div
                    key={message.message_key}
                    className={`${styles.messageItem} ${!message.read ? styles.unread : ''}`}
                    onClick={() => handleMessageClick(message.message_key)}
                  >
                    <div className={styles.messageItemHeader}>
                      <span className={styles.messageItemTitle}>{message.title}</span>
                      {!message.read && <span className={styles.unreadDot}></span>}
                    </div>
                    <div className={styles.messageItemBody}>{message.body}</div>
                    <div className={styles.messageItemTimestamp}>
                      {formatTimestamp(message.created_ts)}
                    </div>
                  </div>
                ))}
              </div>

              {hasMore && (
                <div className={styles.loadMoreContainer}>
                  <button
                    className={styles.loadMoreButton}
                    onClick={handleLoadMore}
                    disabled={loading}
                  >
                    {loading ? (getText('common_text.loading') || 'Loading') + '...' : (getText('messages.load_more') || 'Load more')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MessagesPanel;
